// import api's and mutations
import { mutations } from 'RestAPIs/rapini';

// import components
import { Dialog } from 'components/shared';
import { Box } from '@mui/material';
import MapController from 'components/shared/Map/MapController';
import TravelDetailDialogOverview from './TravelDetailDialogOverview';
import TravelDetailDialogRoutes from './TravelDetailDialogRoutes';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import TravelDetailDialogRoutesHeader from './TravelDetailDialogRoutesHeader';

// import hooks
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

// import providers
import { AzureMapsProvider } from 'react-azure-maps';

// import types
import {
  CorrectTravelMovementPerDayRequest,
  ExpenseClaimResponse,
  ExpenseClaimRouteResponse,
  TravelMovementCorrectionRequest,
  TravelMovementPerDayResponse,
  TravelMovementResponse
} from 'generated/index';
import { LatitudeLongitudePair } from 'components/roster-portal/activity-planning/types';

// import utils
import { formatDate } from 'utils/date';
import { NumberIsFinite } from '../utils';

type Props = {
  claim?: ExpenseClaimResponse | TravelMovementPerDayResponse | null;
  isOpen: boolean;
  onClose?: () => void;
  isTravelMovements?: boolean;
  refetchTravelMovements?: () => void;
};

const TravelDetailDialog = ({
  claim,
  isOpen,
  onClose,
  isTravelMovements = false,
  refetchTravelMovements
}: Props) => {
  const { t } = useTranslation('common');
  const [isEditMode, setEditMode] = useState(false);
  const [editedDistances, setEditedDistances] = useState<{
    [key: number]: string;
  }>({});

  const isDistanceTouched = Object.values(editedDistances).every(
    (value) => value !== undefined && value !== ''
  );

  const methods = useForm();

  const { mutate: correctTravelMovement, isLoading } =
    mutations.useCorrectTravelMovementPerDay({
      onSuccess: () => {
        refetchTravelMovements && refetchTravelMovements();
        handleClose();
        showSuccessSnackbar();
      },
      onError: () => {
        showErrorSnackbar();
      }
    });

  const employmentId = claim?.employment?.id ?? null;
  const date = claim?.date ?? null;
  const isRealised = claim?.status ? claim.status === 'REALIZED' : false;

  if (!claim) return null;

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setEditedDistances({});
  };

  const handleDistanceChange = (index: number, value: string) => {
    setEditedDistances((prev) => ({
      ...prev,
      [index]: value // store raw string
    }));
  };

  const handleSave = async () => {
    const isValid =
      isTravelMovement(claim) &&
      claim.travelMovements.map(
        (_, index) =>
          editedDistances[index] !== undefined &&
          editedDistances[index] !== null &&
          editedDistances[index] !== ''
      );

    if (!isValid) {
      showErrorSnackbar(t('travelMovements.error.distance'));
      return;
    }

    if (
      !employmentId ||
      !date ||
      !claim ||
      !isTravelMovements ||
      !isTravelMovement(claim)
    ) {
      showErrorSnackbar();
      return;
    }

    const travelMovementCorrections = claim.travelMovements
      .map((movement, index) => {
        const correctedDistanceInMeters =
          editedDistances[index] !== undefined &&
          editedDistances[index] !== null
            ? parseFloat(editedDistances[index]) * 1000 // Convert kilometers to meters
            : undefined;

        if (!NumberIsFinite(correctedDistanceInMeters)) return null;

        return {
          correctedDistanceInMeters,
          destinationEventId:
            index === 0 ? movement.destinationEventId : undefined,
          departureEventId: index > 0 ? movement.departureEventId : undefined
        };
      })
      .filter(
        (correction) => correction !== null
      ) as TravelMovementCorrectionRequest[];

    const payload: CorrectTravelMovementPerDayRequest = {
      employmentId,
      date,
      travelMovementCorrections
    };

    correctTravelMovement(payload);
  };

  // Type guards
  const isExpenseClaim = (claim): claim is ExpenseClaimResponse =>
    'routes' in claim;
  const isTravelMovement = (claim): claim is TravelMovementPerDayResponse =>
    'travelMovements' in claim;

  const isManual = !!(claim as TravelMovementPerDayResponse).isManual;
  const isCorrected = !!(claim as TravelMovementPerDayResponse).isCorrected;
  const isMismatch = !!(claim as TravelMovementPerDayResponse).isMismatch;
  const isCredited =
    (claim as TravelMovementPerDayResponse).status === 'CREDITED';

  const isRegisterPrivateKilometers = claim.registerPrivateKilometers ?? false;

  const routes: (ExpenseClaimRouteResponse | TravelMovementResponse)[] =
    isTravelMovements && isTravelMovement(claim)
      ? claim.travelMovements || []
      : isExpenseClaim(claim)
      ? claim.routes || []
      : [];

  // Add missing locations to routes when calculate business routes is disabled
  const processedRoutes = routes.reduce(
    (
      acc: (ExpenseClaimRouteResponse | TravelMovementResponse)[],
      current,
      index
    ) => {
      if (index > 0) {
        const previous = acc[acc.length - 1];
        if (
          'toAddress' in previous &&
          'fromAddress' in current &&
          previous.toAddress !== current.fromAddress
        ) {
          acc.push({
            fromAddress: previous.toAddress,
            toAddress: current.fromAddress,
            fromDescription: previous.toDescription,
            toDescription: current.fromDescription,
            distanceInMeters: 0,
            type: 'BUSINESS'
          });
        }
      }
      acc.push(current);
      return acc;
    },
    []
  );

  const pointSet = routes.map(
    ({ points }) => JSON.parse(points || '[]') as LatitudeLongitudePair[]
  );

  const showRoutes =
    !isTravelMovements ||
    !(claim as TravelMovementPerDayResponse).hasFixedDistances;

  const handleClose = () => {
    onClose?.();
    setEditedDistances({});
    setEditMode(false);
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={isOpen && !!claim}
        title={`${t('travelExpenseClaims.details')} - ${formatDate(
          claim.date
        )} - ${claim.employment.employeeName}`}
        handleClose={handleClose}
        maxWidth="medium"
        slotProps={{
          paper: {
            sx: {
              minWidth: showRoutes ? '75%' : 'fit-content',
              minHeight: showRoutes ? '75%' : 'fit-content',
              maxWidth: '600px',
              maxHeight: 'calc(100% - 200px)'
            }
          }
        }}
      >
        <Box
          display="flex"
          gap={2}
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <Box
            sx={{
              width: '50%',
              minWidth: '500px'
            }}
          >
            <TravelDetailDialogOverview
              claim={claim}
              routes={routes}
              isTravelMovements={isTravelMovements}
              isManual={isManual}
              isCorrected={isCorrected}
              isMismatch={isMismatch}
              isCredited={isCredited}
              isRegisterPrivateKilometers={isRegisterPrivateKilometers}
            />

            {showRoutes && (
              <TravelDetailDialogRoutesHeader
                routes={routes}
                isTravelMovements={isTravelMovements}
                isManual={isManual}
                isEditMode={isEditMode}
                toggleEditMode={toggleEditMode}
                handleSave={handleSave}
                isLoading={isLoading}
                isRealised={isRealised}
                isCredited={isCredited}
                isDistanceTouched={isDistanceTouched}
              />
            )}

            {showRoutes && (
              <Box
                sx={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: '500px'
                }}
              >
                {processedRoutes.map((route, i) => {
                  const isLast = isTravelMovements
                    ? processedRoutes.length === i + 1
                    : isExpenseClaim(claim) && claim.routes.length === i + 1;

                  return (
                    <TravelDetailDialogRoutes
                      key={i}
                      route={route}
                      isTravelMovements={isTravelMovements}
                      isLast={isLast}
                      i={i}
                      isManual={isManual}
                      isEditMode={isEditMode}
                      editedDistance={editedDistances[i]}
                      isRegisterPrivateKilometers={
                        isExpenseClaim(claim)
                          ? claim.registerPrivateKilometers
                          : false
                      }
                      onDistanceChange={handleDistanceChange}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          {showRoutes && (
            <Box
              sx={{
                width: '50%',
                minHeight: '750px',
                minWidth: '500px'
              }}
            >
              <AzureMapsProvider>
                <MapController pointSet={pointSet} />
              </AzureMapsProvider>
            </Box>
          )}
        </Box>
      </Dialog>
    </FormProvider>
  );
};

export default TravelDetailDialog;
